import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'pending', label: 'Received', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
  )},
  { key: 'confirmed', label: 'Confirmed', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  )},
  { key: 'preparing', label: 'Preparing', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" /></svg>
  )},
  { key: 'ready', label: 'Ready', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  )},
  { key: 'out_for_delivery', label: 'On the Way', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0H18M3.375 14.25h3.75m0 0V11.25m0 3H18V7.5a1.125 1.125 0 00-1.125-1.125H11.25" /></svg>
  )},
  { key: 'delivered', label: 'Delivered', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  )},
];

const stepIndex = (status) => STEPS.findIndex((s) => s.key === status);

const OrderTracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  const loadOrder = async () => {
    try {
      const res = await axios.get(`${API}/api/orders/${orderId}/track`);
      setOrder(res.data);
      setLastRefresh(Date.now());
    } catch {
      setError('Order not found');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrder();
    const interval = setInterval(loadOrder, 20000);
    return () => clearInterval(interval);
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-8 h-8 border-2 border-[#C97A2B] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center px-4" data-testid="order-not-found">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#D9D9D6]/30 flex items-center justify-center mb-4">
            <svg className="w-7 h-7 text-[#6B705C]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
          </div>
          <h2 className="text-xl text-[#2F2F2F] mb-2" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>Order Not Found</h2>
          <p className="text-[#6B705C] text-sm mb-6">We couldn't find an order with that ID.</p>
          <button
            onClick={() => navigate('/order-lookup')}
            className="px-6 py-2.5 bg-[#2F2F2F] text-white text-xs tracking-[0.15em] uppercase hover:bg-[#C97A2B] transition-colors"
            data-testid="back-to-lookup-btn"
          >
            Look Up Order
          </button>
        </div>
      </div>
    );
  }

  const current = stepIndex(order.status);
  const isCancelled = order.status === 'cancelled';
  const isDelivered = order.status === 'delivered';

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="order-tracking-page">
      <div className="max-w-2xl mx-auto px-4 py-12 md:py-16">
        {/* Header */}
        <motion.div className="text-center mb-10" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-[#6B705C] text-xs tracking-[0.3em] uppercase mb-3">Order Tracking</p>
          <h1 className="text-3xl md:text-4xl text-[#2F2F2F] mb-2" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
            #{orderId.slice(0, 8).toUpperCase()}
          </h1>
          {order.estimatedDeliveryTime && !isDelivered && !isCancelled && (
            <p className="text-[#C97A2B] font-medium">
              Estimated arrival: {new Date(order.estimatedDeliveryTime).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </motion.div>

        {/* Status Timeline */}
        <motion.div
          className="bg-white rounded-lg border border-[#D9D9D6] p-6 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          data-testid="status-timeline"
        >
          {isCancelled ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-3">
                <svg className="w-7 h-7 text-[#B65A3A]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </div>
              <p className="text-[#B65A3A] font-medium uppercase tracking-wide text-sm">Order Cancelled</p>
            </div>
          ) : (
            <div className="space-y-0">
              {STEPS.map((step, i) => {
                const done = i < current;
                const active = i === current;
                const future = i > current;
                return (
                  <div key={step.key} className="flex items-start gap-4">
                    {/* Connector line + circle */}
                    <div className="flex flex-col items-center">
                      <motion.div
                        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                          done ? 'bg-[#C97A2B] border-[#C97A2B] text-white' :
                          active ? 'bg-white border-[#C97A2B] text-[#C97A2B]' :
                          'bg-[#F5F3EF] border-[#D9D9D6] text-[#D9D9D6]'
                        }`}
                        animate={active ? { scale: [1, 1.1, 1] } : {}}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        {done ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                        ) : step.icon}
                      </motion.div>
                      {i < STEPS.length - 1 && (
                        <div className={`w-[2px] h-8 ${done ? 'bg-[#C97A2B]' : 'bg-[#D9D9D6]'}`} />
                      )}
                    </div>
                    {/* Label */}
                    <div className={`pt-2 pb-4 ${future ? 'opacity-40' : ''}`}>
                      <p className={`text-sm font-medium ${active ? 'text-[#C97A2B]' : done ? 'text-[#2F2F2F]' : 'text-[#6B705C]'}`}>
                        {step.label}
                      </p>
                      {active && (
                        <motion.p
                          className="text-xs text-[#6B705C] mt-0.5"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                        >
                          Current status
                        </motion.p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* Order Details */}
        <motion.div
          className="bg-white rounded-lg border border-[#D9D9D6] overflow-hidden mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          data-testid="order-details"
        >
          <div className="px-5 py-4 border-b border-[#D9D9D6]/50">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#6B705C]">Order Items</p>
          </div>
          <div className="px-5 py-3 space-y-3">
            {order.items?.map((item, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-[#2F2F2F]">{item.name} <span className="text-[#6B705C]">x{item.quantity}</span></span>
                <span className="text-[#2F2F2F] font-medium">{item.price}</span>
              </div>
            ))}
          </div>
          <div className="px-5 py-4 bg-[#F5F3EF]/60 border-t border-[#D9D9D6]/50 space-y-1">
            <div className="flex justify-between text-sm text-[#6B705C]">
              <span>Subtotal</span><span>{order.subtotal}</span>
            </div>
            <div className="flex justify-between text-sm text-[#6B705C]">
              <span>Delivery</span><span>{order.deliveryFee}</span>
            </div>
            <div className="flex justify-between text-base font-bold text-[#2F2F2F] pt-2 border-t border-[#D9D9D6]/50">
              <span>Total</span>
              <span style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>{order.totalPrice}</span>
            </div>
          </div>
        </motion.div>

        {/* Delivery Info */}
        <motion.div
          className="bg-white rounded-lg border border-[#D9D9D6] p-5 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          data-testid="delivery-info"
        >
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#6B705C] mb-3">Delivery Details</p>
          <div className="space-y-2 text-sm">
            <p className="text-[#2F2F2F]"><span className="text-[#6B705C]">Address:</span> {order.customer?.address}</p>
            <p className="text-[#2F2F2F]"><span className="text-[#6B705C]">Payment:</span> {order.paymentMethod || 'Cash on Delivery'}</p>
          </div>
        </motion.div>

        {/* Contact + Auto-refresh */}
        <motion.div
          className="text-center space-y-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <a
            href="tel:+358403779200"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#D9D9D6] rounded text-sm text-[#2F2F2F] hover:border-[#C97A2B] hover:text-[#C97A2B] transition-colors"
            data-testid="call-restaurant-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
            Call +358 40 377 9200
          </a>
          <p className="text-[#D9D9D6] text-xs">Auto-refreshes every 20 seconds</p>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderTracking;
